import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getDataBS } from "../../utils/awsService";
import BlogCard from "./blogCard";
import Pagination from "../common/Pagination";
import "./blogList.css";
import imageHolder from "../../assets/images/logos/mentologo.png";

const BlogList = ({ refreshPosts }) => {
  const navigate = useNavigate();
  const [blogPosts, setBlogPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        setPostsLoading(true);

        const responseData = await getDataBS("/blog");

        // Extract blog posts data
        let blogPostsData = responseData.data["data"];

        // Transform data to extract and normalize specific fields
        const processedBlogPosts = blogPostsData
        
          .map((post) => ({
            id: post.blogId|| null,
            title: post.title || "Untitled",
            content: post.content || "",
            author: post.author || "Unknown",
            createdAt: post.createdAt || new Date().toISOString(),
            category: post.category || "Uncategorized",
            featuredImage: post.featuredImage || "",
          }))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        // Enhance blog posts with additional formatting for BlogCard
        // Helper function to strip HTML tags
        const stripHtmlTags = (html) => {
          return html.replace(/<[^>]*>/g, '').trim();
        };

// Helper function to get clean text excerpt
        const getCleanExcerpt = (content, start = 0, length = 125) => {
          const cleanText = stripHtmlTags(content);
          return cleanText.substring(start, length) + (cleanText.length > length ? "..." : "");
        };

        const formattedBlogPosts = processedBlogPosts.map((post) => ({
          ...post,
          altText: post.title,
          highlightedWord: post.title.split(" ").pop(),
          tags: [
            ...(post.category
                ? post.category.split(",").map((cat) => ({
                  text: cat.trim(),
                  color: getCategoryColor(cat.trim()),
                }))
                : [
                  {
                    text: "Uncategorized",
                    color: getCategoryColor("Uncategorized"),
                  },
                ]),
            {
              text: getMonthName(new Date(post.createdAt)),
              color: "light-blue",
            },
          ],
          intro: getCleanExcerpt(post.content, 0, 125), // Use the helper function
          date: new Date(post.createdAt).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          }),
          image: post.featuredImage || imageHolder,
        }));

        setBlogPosts(formattedBlogPosts);
        setError(null);
        
        // Reset pagination to first page when posts are refreshed
        setCurrentPage(1);
      } catch (error) {
        console.error("Error fetching blog posts:", error);
        setBlogPosts([]);
        setError("Failed to load blog posts. Please try again later.");
      } finally {
        setPostsLoading(false);
      }
    };

    fetchBlogPosts();
  }, [refreshPosts]);

  // Helper function to get a color based on category
  const getCategoryColor = (category) => {
    if (!category) return "blue";

    const categoryColors = {
      "Mental Health": "green",
      Healthcare: "green",
      Stress: "red",
      Anxiety: "red",
      Article: "pink",
      Depression:"blue" ,
      "Stress and Anxiety":"red",
      "stress and anxiety":"red",
    };

    // Default to blue if no matching category
    return categoryColors[category] || "blue";
  };

  // Helper function to get month name
  const getMonthName = (date) => {
    return date.toLocaleString("en-US", { month: "short" });
  };

  // Handle click on a blog post - now redirects to blog detail page
  const handleArticleClick = (id) => {
    navigate(`/blog/${id}`);
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculate pagination
  const totalPages = Math.ceil(blogPosts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPosts = blogPosts.slice(startIndex, endIndex);

  // Loading state
  if (postsLoading) {
    return (
      <div className="blog-list-container">
        <div className="blog-loading">
          <p>Loading blog posts...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="blog-list-container">
        <div className="blog-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (blogPosts.length === 0) {
    return (
      <div className="blog-list-container">
        <div className="blog-empty">
          <p>No blog posts available at the moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-list-container">
      <div className="blog-list">
        {currentPosts.map((post) => (
          <div className="blog-item" key={post.id}>
            <BlogCard
              image={post.image}
              altText={post.altText}
              title={post.title.toUpperCase()}
              subtitle={post.category ? post.category.toUpperCase() : ""}
              highlightedWord={post.highlightedWord}
              tags={post.tags}
              intro={post.intro}
              date={post.date}
              onClick={() => handleArticleClick(post.id)}
            />
          </div>
        ))}
      </div>
      
      {blogPosts.length > itemsPerPage && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={itemsPerPage}
          totalItems={blogPosts.length}
        />
      )}
    </div>
  );
};

export default BlogList;
